import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  GestorDashboardBreakdownList,
  GestorDashboardMetricCard,
  type GestorDashboardBreakdownListRow,
} from '@/components/Gestores/GestorDashboardWidgets'
import { GestorDashboardClientScopePanel } from '@/components/Gestores/GestorDashboardClientScopePanel'
import { buildGestorDashboardTaskPath } from '@/components/Gestores/gestorDashboardNavigation'
import { formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'
import type {
  GestorDashboardClientsView,
  GestorDashboardSummary,
  GestorDashboardTimeEntry,
} from '@/components/Gestores/gestorDashboardTypes'

type Props = {
  clients: GestorDashboardClientsView
  summary: GestorDashboardSummary
  entries: GestorDashboardTimeEntry[]
  selectedWorkspace: string
  selectedPhase: string
  period: { createdFrom: string; createdTo: string }
  squadId?: number | null
  loading?: boolean
  onWorkspaceSelect: (workspace: string) => void
  onPhaseSelect: (phase: string) => void
  onScopeClear: () => void
}

export function GestorDashboardClientView({
  clients,
  summary,
  entries,
  selectedWorkspace,
  selectedPhase,
  period,
  squadId = null,
  loading = false,
  onWorkspaceSelect,
  onPhaseSelect,
  onScopeClear,
}: Props) {
  const navigate = useNavigate()
  const scopePanelRef = useRef<HTMLElement>(null)

  const selectedScope = selectedWorkspace
    ? ({ kind: 'workspace' as const, label: selectedWorkspace })
    : selectedPhase
      ? ({ kind: 'phase' as const, label: selectedPhase })
      : null

  const openTask = (taskId: number) => {
    navigate(buildGestorDashboardTaskPath(taskId, { ...period, squadId }))
  }

  const activeWorkspaceKey = useMemo(() => {
    if (!selectedWorkspace) return null
    return clients.byWorkspace.find((row) => row.label === selectedWorkspace)?.key ?? null
  }, [clients.byWorkspace, selectedWorkspace])

  const activePhaseKey = useMemo(() => {
    if (!selectedPhase) return null
    return clients.byPhase.find((row) => row.label === selectedPhase)?.key ?? null
  }, [clients.byPhase, selectedPhase])

  useEffect(() => {
    if (!selectedScope) return
    scopePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selectedScope])

  return (
    <div className='space-y-4'>
      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <GestorDashboardMetricCard label='Clientes atendidos' value={String(summary.workspaceCount)} />
        <GestorDashboardMetricCard label='Horas na carteira' value={formatEffortMinutes(summary.totalEffortMinutes)} />
        <GestorDashboardMetricCard label='Maior consumo' value={clients.topConsumers[0]?.label ?? '—'} />
        <GestorDashboardMetricCard
          label='Horas top cliente'
          value={formatEffortMinutes(clients.topConsumers[0]?.effortMinutes ?? 0)}
          tone='success'
        />
      </div>

      <div className='grid gap-3 xl:grid-cols-2'>
        <GestorDashboardBreakdownList
          title='Esforço por cliente'
          rows={clients.byWorkspace}
          onRowClick={(row: GestorDashboardBreakdownListRow) => onWorkspaceSelect(row.label)}
          activeRowKey={activeWorkspaceKey}
        />
        <GestorDashboardBreakdownList
          title='Esforço por etapa'
          rows={clients.byPhase}
          onRowClick={(row: GestorDashboardBreakdownListRow) => onPhaseSelect(row.label)}
          activeRowKey={activePhaseKey}
        />
      </div>

      {selectedScope ? (
        <GestorDashboardClientScopePanel
          ref={scopePanelRef}
          scope={selectedScope.kind}
          label={selectedScope.label}
          entries={entries}
          loading={loading}
          onClear={onScopeClear}
          onTaskOpen={openTask}
        />
      ) : (
        <>
          <div className='grid gap-3 xl:grid-cols-2'>
            <GestorDashboardBreakdownList
              title='Clientes com maior consumo'
              rows={clients.topConsumers}
              onRowClick={(row) => onWorkspaceSelect(row.label)}
            />
            <GestorDashboardBreakdownList
              title='Clientes com menor consumo'
              rows={clients.bottomConsumers}
              onRowClick={(row) => onWorkspaceSelect(row.label)}
            />
          </div>
          <p className='text-xs text-text-muted'>
            Clique em um cliente ou etapa acima para ver tarefas e apontamentos segmentados.
          </p>
        </>
      )}
    </div>
  )
}
